import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  WEB_COMPONENT_PHASR_CATEGORIES_WIDGET_TEMPLATE,
  WEB_COMPONENT_PHASR_TAMPERING_TOOLS,
  WEB_COMPONENT_PHASR_MINERS,
  WEB_COMPONENT_PHASR_HACKTOOLS,
  WEB_COMPONENT_PHASR_REMOTE_ADMIN_TOOLS,
  WEB_COMPONENT_PHASR_OFF_LAND_BINARIES,
} from '../../../../webcomponents.constants';
import {
  DonutChartWidgetPayload,
  PhasrCategoryPayload,
  PhasrRecommendationData,
  PhasrSlicePayload,
  PhasrSlicesConfigData,
  PhasrSlicesDetailsData,
} from '@nirvana/shared/dtos/types/widgets/common';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { html, TemplateResult } from 'lit';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { isEmpty } from 'lodash';
import { styleMap } from 'lit/directives/style-map.js';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { SpinnerWidth } from '../../../../ui/gzProgressSpinner/types';
import { formatLargeNumber } from '../../../../utils/formatter.utils';
import { PhasrRecommendationSideMenuWebComponent } from '../../../../phasrSideMenus/phasrRecommendationSideMenu';
import { DashboardEventsListeners } from '../../../../genericDashboard/types/types';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { ColumnKeys } from '../../../../phasrRecommendations/phasrRecommendationsGrid/types/grid';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { CategoryTypes, getPhasrCategoryTypesName } from '../../../../types/common/phasrTypes';
import { RESTRICT_ACTION } from '../../../../phasrRecommendations/phasrRecommendationsGrid/types';
import { PhasrRenderIconSet } from '../../../../phasrRecommendations/uiUtils/iconSet';
import { PhasrIcons } from '../../../../phasrRecommendations/uiUtils/types';
import { ButtonColor, ButtonSize } from '../../../../ui/gzIconButton/types';

@customElement(WEB_COMPONENT_PHASR_CATEGORIES_WIDGET_TEMPLATE)
export class PhasrCategoriesWidgetTemplateWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: PhasrCategoryPayload;

  private readonly sectionNrOfItems = 4;
  private readonly chartContainerSize = 220; // px
  private readonly chartToContainerRatio = 0.75;
  private readonly chartLineWidth = 12; // px
  private readonly legendTextMap = new Map<keyof PhasrSlicePayload, [string, string, string]>([
    ['restricted',
      [
        translateFn('Restricted behavioral profiles'),
        'var(--green-teal-chart-400)',
        'Indicates behavioral profiles that have been restricted either manually through Direct Control or automatically by Autopilot. The restrictions were applied based on observed user behavior. It is advised to review these profiles periodically to ensure that the restrictions remain appropriate and valid.'
      ]
    ],
    ['active',
      [
        translateFn('Behavioral profiles using such tools'),
        'var(--yellow-chart-050)',
        'Indicates behavioral profiles that have used {tool_category} either legitimately or otherwise, regularly or occasionally. It is recommended to analyze the usage to determine whether access to these tools should be restricted.'
      ],
    ],
    ['inactive',
      [
        translateFn('Behavioral profiles not using such tools'),
        'var(--red-chart-400)',
        'It is recommended to restrict access for these users to reduce the attack surface and mitigate potential attack vectors, with no business impact.'
      ],
    ],
  ]);
  private readonly legendDetailsTextMap = new Map<keyof PhasrSlicesDetailsData, [string]>([
    ['autopilot',
      [
        translateFn('Autopilot')
      ]
    ],
    ['directControl',
      [
        translateFn('Direct control')
      ]
    ],
  ]);

  private dictionary = [];

  protected phasrCategoryType: CategoryTypes = CategoryTypes.UNKNOWN;

  static get styles() {
    return styles;
  }

  public willUpdate(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.willUpdate(_changedProperties);
    this.dictionary = Application.Main.getPhasrDictionary();
  }

  protected renderLoader(): TemplateResult {
    return this.renderContent();
  }

  public injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
  }

  protected renderContent(): TemplateResult {
    const chartContainerStyles = { width: `${this.chartContainerSize}px`, height: `${this.chartContainerSize}px` };
    let viewAllButton = html``;
    if (this.state !== WidgetStateFrontEnd.IS_LOADING && this.state !== WidgetStateFrontEnd.IS_EMPTY) {
      viewAllButton = html`
        <div class='viewAllSection' id='viewAllButtonWrapper'>
          <gz-button
            id='viewAllButton'
            class='d-flex align-items-end'
            type='${ButtonTypes.QUIET}'
            buttonheight='${ButtonHeights.SMALL}'
            buttonfontsize='${ButtonFontSizes.MEDIUM}'
            lowercase
            @click=${() => this.handleViewAllClick()}
          >
            ${translateFn('View all')}
          </gz-button>
        </div>
      `;
    }

    return html`
      <div class="gz-phasr-generic-categories-content">
        <div class="top-container">
          <div class="chart-container" style="${styleMap(chartContainerStyles)}">${this.renderChart()}</div>
          <div class="legend-container">${this.renderLegend()}</div>
        </div>
        ${this.renderRecommendations()}
      </div>
      ${viewAllButton}
    `;
  }

  private renderChart(): TemplateResult {
    if (!this.shouldRenderRecommendations() || this.state === WidgetStateFrontEnd.IS_LOADING) {
      return this.renderContinuousChart();
    }
    return this.renderSlicedChart();
  }

  private renderContinuousChart(): TemplateResult {
    const shouldRenderEmptyChart = [
      WidgetStateFrontEnd.IS_EMPTY,
      WidgetStateFrontEnd.IS_ERROR,
      WidgetStateFrontEnd.IS_LOADING,
    ].includes(this.state);
    const progress = this.payload?.learning?.progress ?? 0;
    const chartBigText = shouldRenderEmptyChart ? '- -' : `${progress}%`;
    const chartSmallText = shouldRenderEmptyChart ? '...' : translateFn('learning');
    const chartFooterText = shouldRenderEmptyChart
      ? ''
      : translateFn('Remaining days: {days}', { days: this.payload?.learning?.estimatedRemainingDays ?? 0 });
    const chartWidth = this.chartContainerSize * this.chartToContainerRatio - this.chartLineWidth;

    return html`
      <gz-text class="big-text" fontSize="${FontSizes.HEADING_3}">${chartBigText}</gz-text>
      <gz-text class="small-text" fontSize="${FontSizes.HEADING_6}">${chartSmallText}</gz-text>
      <gz-progress-spinner
        value="${progress}"
        spinnerWidth="${SpinnerWidth.CUSTOM}"
        customSpinnerWidthValue="${chartWidth}"
      ></gz-progress-spinner>
      <gz-text class="remaining-days" fontSize="${FontSizes.HEADING_6}">${chartFooterText}</gz-text>
    `;
  }

  private renderSlicedChart(): TemplateResult {
    const slices = Object.entries(this.payload?.slices ?? {}) as [keyof PhasrSlicePayload, PhasrSlicesConfigData][];
    const chartPayload: DonutChartWidgetPayload = {
      donutChart: {
        data: {
          slices: slices.map(([key, slice]) => ({
            id: key,
            value: slice.userProfilesCount,
          })),
        },
        config: {
          slices: slices.map(([key, _]) => ({
            id: key,
            label: key,
            color: this.legendTextMap.get(key)?.[1] ?? '',
          })),
          label: translateFn('behavioral profiles'),
          hideLegend: true,
          chartSize: this.chartContainerSize * this.chartToContainerRatio + this.chartLineWidth * 2,
          lineWidth: this.chartLineWidth,
          labelTextMultiline: true
        },
      }
    };
    return html`
      <gz-donut-chart-widget
        .state="${WidgetStateFrontEnd.IS_ACTIVE}"
        .payload="${chartPayload}"
      ></gz-donut-chart-widget>
    `;
  }

  private renderLegend(): TemplateResult {
    if (this.state === WidgetStateFrontEnd.IS_LOADING) {
      return html`${Array.from({ length: this.legendTextMap.size }, () => this.renderLoaderItem())}`;
    }

    let displayText = null;
    if (this.state === WidgetStateFrontEnd.IS_EMPTY || this.state === WidgetStateFrontEnd.IS_ERROR) {
      displayText = translateFn('Monitoring is disabled for this activity type.');
    } else if (!this.shouldRenderRecommendations()) {
      const users = this.payload?.learning?.targetUsersCount ?? 0;
      const behavioralPatterns = this.payload?.learning?.behavioralPatternsCount ?? 0;
      displayText = translateFn(
        `Learning ${behavioralPatterns} behavioral patterns for ${users} unique behavioral profiles.`
      );
    }
    if (displayText) {
      return html`<gz-text class="empty-legend-text" fontSize="${FontSizes.HEADING_6}">${displayText}</gz-text>`;
    }

    return html`
      ${Object.entries(this.payload?.slices ?? {}).map(([key, slice]) =>
        this.renderLegendItem(key as keyof PhasrSlicePayload, slice)
      )}
      ${this.renderLearningBanner()}
    `;
  }

  private renderLearningBanner(): TemplateResult | undefined {
    const targetUsersCount = this.payload.learning?.targetUsersCount ?? 0;
    if (!this.payload?.isLearning && targetUsersCount > 0) {
      return html` <phasr-gathering-data-info
        nrUsers="${targetUsersCount}"
        behavioralPatterns="${this.payload.learning?.behavioralPatternsCount ?? 0}"
        .showRemainingDays="${false}"
        .isLearning="${false}"
        class="learning-info-banner"
      ></phasr-gathering-data-info>`;
    }
  }

  private renderLegendItem(
    sliceKey: keyof PhasrSlicePayload,
    slice: PhasrSlicesConfigData | undefined
  ): TemplateResult {
    const [text, color, tooltip] = this.legendTextMap.get(sliceKey) ?? '';
    const count = slice?.userProfilesCount ?? 0;
    const showCountTooltip = count >= 1000; // because count will be formatted as 1k, 1m, etc.
    const translatedTooltip = translateFn(tooltip,
      {
        tool_category: getPhasrCategoryTypesName().find(item => item.value === this.phasrCategoryType)?.label ?? '',
      }
    );

    let legendSubCategories = html``;
    if (sliceKey === 'restricted') {
      legendSubCategories = html`
      ${Object.entries(this.payload?.slices?.restricted.details ?? {}).map(([key, value]) =>
        this.renderSubLegendItem(key as keyof PhasrSlicesDetailsData, value)
      )}`;
    }

    return html`<div class="legend-item">
      <div class="legend-item--color" style="background-color: ${color}"></div>
      <gz-tooltip class="legend-item--slice-tooltip" tooltipText="${translatedTooltip}">
        <gz-text class="legend-item--text" fontSize="${FontSizes.HEADING_6}">${text}</gz-text>
      </gz-tooltip>
      <gz-tooltip class="legend-item--count" tooltipText="${count}" ?isEllipsis="${!showCountTooltip}">
        ${PhasrRenderIconSet.createIcon(PhasrIcons.BEHAVIORAL_PROFILES_ICON, ButtonColor.SECONDARY, ButtonSize.SIZE_24, 'svg-icon', 'icon-value')}
        <gz-text class="legend-item--value" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.BOLD}">
          ${formatLargeNumber(count)}
        </gz-text>
      </gz-tooltip>
    </div>
      ${legendSubCategories}`;
  }

  private renderSubLegendItem(
    sliceKey: keyof PhasrSlicesDetailsData,
    slice: PhasrSlicesConfigData | undefined
  ): TemplateResult {
    const [text, tooltip] = this.legendDetailsTextMap.get(sliceKey) ?? '';
    const count = slice?.userProfilesCount ?? 0;
    const showCountTooltip = count >= 1000; // because count will be formatted as 1k, 1m, etc.

    return html`<div class="sub-legend-item">
      <div class="sub-legend-item--color"></div>
      <gz-tooltip class="legend-item--slice-tooltip" tooltipText="${tooltip}">
          <gz-text class="legend-item--text" fontSize="${FontSizes.PARAGRAPH_12}">${text}</gz-text>
      </gz-tooltip>
      <gz-tooltip class="legend-item--count" tooltipText="${count}" ?isEllipsis="${!showCountTooltip}">
        ${PhasrRenderIconSet.createIcon(PhasrIcons.BEHAVIORAL_PROFILES_ICON, ButtonColor.SECONDARY, ButtonSize.SIZE_24, 'svg-icon', 'icon-value')}
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}">
          ${formatLargeNumber(count)}
        </gz-text>
      </gz-tooltip>
    </div>`;
  }

  private renderRecommendations(): TemplateResult | undefined {
    const items = Array.from({ length: this.sectionNrOfItems }, (_, i) =>
      this.state === WidgetStateFrontEnd.IS_LOADING ? this.renderLoaderItem() : this.renderRecommendationItem(i)
    );
    return html`<div class="recommendations-container">${items}</div>`;
  }

  private renderLoaderItem(): TemplateResult {
    return html`<gz-loader rows="1"></gz-loader>`;
  }

  private handleItemClick(recommendationId: string | null): void {
    // will be done in the future when we have the object id of the recommendation
    if (!recommendationId) {
      return;
    }

    const recommendationDetailsComponent = new PhasrRecommendationSideMenuWebComponent();
    recommendationDetailsComponent.setRecommendationId(recommendationId);
    recommendationDetailsComponent.shouldGetDataFromBackend = true;
    recommendationDetailsComponent.injectDependencies(this.dependencies);
    recommendationDetailsComponent.start().catch(console.error);

    const eventDetail = { component: recommendationDetailsComponent };
    this.dispatchEvent(
      new CustomEvent(DashboardEventsListeners.WIDGET_DETAILS_CLICKED, {
        detail: eventDetail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderRecommendationItem(index: number): TemplateResult {
    const { state, name, surfacePercentageReduction, userProfilesCount, recommendationId } = this.getItemData(index);
    return html`
      <gz-widget-section-with-icons-and-percentage-item
        name="${name}"
        percentageValue="${surfacePercentageReduction}"
        .state="${state}"
        .iconValue="${userProfilesCount}"
      ></gz-widget-section-with-icons-and-percentage-item>
    `;
  }

  private getItemData(index: number): {
    state: WidgetStateFrontEnd;
    name: string;
    surfacePercentageReduction: number;
    userProfilesCount: number;
    recommendationId: string | null;
  } {
    if (this.isRecommendationEmpty(index)) {
      return {
        state: WidgetStateFrontEnd.IS_EMPTY,
        name: '',
        surfacePercentageReduction: 0,
        userProfilesCount: 0,
        recommendationId: null,
      };
    }
    const item = this.payload?.recommendations?.[index] as PhasrRecommendationData;
    return {
      state: this.state,
      name: this.dictionary[item.recommendationId]?.[item.recommendationType === RESTRICT_ACTION ? 'rule_recommendation' : 'rule_allow_name'] ?? 'N/A',
      surfacePercentageReduction: item.surfacePercentageReduction,
      userProfilesCount: item.userProfilesCount,
      recommendationId: item.recommendationId,
    };
  }

  private isRecommendationEmpty(index: number): boolean {
    return isEmpty(this.payload) || index >= (this.payload?.recommendations?.length ?? 0);
  }

  private shouldRenderRecommendations(): boolean {
    return (
      !this.payload?.isLearning &&
      this.state !== WidgetStateFrontEnd.IS_ERROR &&
      this.state !== WidgetStateFrontEnd.IS_EMPTY
    );
  }

  private handleViewAllClick(): void {
    const params: {
      filters: Partial<Record<ColumnKeys, unknown>>[];
    } = {
      filters: [
        {
          [ColumnKeys.COMPANY]: { exactMatch: this.payload.companyId },
        },
        {
          [ColumnKeys.CREATED_ON]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            }
          }
        },
        {
          [ColumnKeys.PHASR_CATEGORIES]: { in: [this.phasrCategoryType] }
        }
      ]
    };
    Application.lib.Navigation.openAppPageInNewTab('/phasr-recommendations', params);
  }
}

@customElement(WEB_COMPONENT_PHASR_HACKTOOLS)
export class PhasrHackToolsWebComponent extends PhasrCategoriesWidgetTemplateWebComponent {
  protected phasrCategoryType = CategoryTypes.HACK_TOOl;
}

@customElement(WEB_COMPONENT_PHASR_MINERS)
export class PhasrMinersWebComponent extends PhasrCategoriesWidgetTemplateWebComponent {
  protected phasrCategoryType = CategoryTypes.MINER;
}

@customElement(WEB_COMPONENT_PHASR_REMOTE_ADMIN_TOOLS)
export class PhasrRemoteAdminToolsWebComponent extends PhasrCategoriesWidgetTemplateWebComponent {
  protected phasrCategoryType = CategoryTypes.REMOTE_TOOL;
}

@customElement(WEB_COMPONENT_PHASR_TAMPERING_TOOLS)
export class PhasrTamperingToolsWebComponent extends PhasrCategoriesWidgetTemplateWebComponent {
  protected phasrCategoryType = CategoryTypes.TAMPERING_TOOl;
}

@customElement(WEB_COMPONENT_PHASR_OFF_LAND_BINARIES)
export class PhasrOffLandBinariesWebComponent extends PhasrCategoriesWidgetTemplateWebComponent {
  protected phasrCategoryType = CategoryTypes.LOL_BIN;
}
